import { Link } from 'react-router-dom';
import {
  InventoryStatusBadge,
  ItemReservationHint,
} from '@/modules/inventory/components';
import { useCategories, useItems, useSubcategories } from '@/modules/inventory/hooks';
import { useItemReservationOverlays } from '@/modules/reservations/hooks';
import {
  INVENTORY_MODE_LABELS,
} from '@/modules/inventory/types';
import { buildCalendarPath } from '@/modules/calendar/utils';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

export function ItemsPage() {
  const { items, loading, error } = useItems();
  const { categories } = useCategories();
  const { subcategories } = useSubcategories();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('inventory.write');
  const canViewCalendar = hasPermission('calendar.read');
  const { overlays } = useItemReservationOverlays();

  const categoryName = (id: string) =>
    categories.find((c) => c.id === id)?.name ?? '—';
  const subcategoryName = (id: string | null) =>
    id ? (subcategories.find((s) => s.id === id)?.name ?? '—') : '—';

  return (
    <section>
      <div className="inventory-page-header">
        <h1>Inventario</h1>
        {canWrite ? (
          <Link
            to={`${modulePaths.inventory}/nuevo`}
            className="auth-button inventory-page-header__btn"
          >
            Nuevo artículo
          </Link>
        ) : null}
      </div>

      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {error ? <p className="auth-error">{error}</p> : null}

      <table className="users-table">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Nombre</th>
            <th>Categoría</th>
            <th>Modo</th>
            <th>Estado</th>
            <th>Reserva activa</th>
            <th>Stock</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className={item.status === 'inactive' ? 'row-muted' : ''}>
              <td>
                <code>{item.sku}</code>
              </td>
              <td>{item.name}</td>
              <td>
                {categoryName(item.categoryId)}
                {item.subcategoryId ? (
                  <span className="auth-muted">
                    {' '}
                    / {subcategoryName(item.subcategoryId)}
                  </span>
                ) : null}
              </td>
              <td>{INVENTORY_MODE_LABELS[item.mode]}</td>
              <td>
                <InventoryStatusBadge status={item.status} />
              </td>
              <td>
                {item.mode === 'rental' && overlays.has(item.id) ? (
                  <ItemReservationHint overlay={overlays.get(item.id)!} />
                ) : (
                  '—'
                )}
              </td>
              <td>
                {item.trackStock
                  ? `${item.stockQuantity} ${item.stockUnit}`
                  : '—'}
              </td>
              <td className="users-actions-cell">
                <Link
                  to={`${modulePaths.inventory}/${item.id}`}
                  className="link-button"
                >
                  {canWrite ? 'Editar' : 'Ver'}
                </Link>
                {canViewCalendar && item.mode === 'rental' ? (
                  <Link
                    to={buildCalendarPath({ itemId: item.id })}
                    className="link-button"
                  >
                    Agenda
                  </Link>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && items.length === 0 ? (
        <p className="auth-muted">
          No hay artículos. Creá categorías y tu primer artículo.
        </p>
      ) : null}
    </section>
  );
}
